import { lazy, Suspense, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { create } from 'zustand';
import { api, ApiError, type Board, type Session, type Space, type Team } from '../../lib/api';
import { useRefreshSession } from '../../lib/session';
import { useTeamSpaces } from '../../lib/spaces';
import { errorText } from '../../i18n';
import { Modal, toast } from '@vatman/ui';
import { keys, type TrackerProject } from '../tracker/model';
import { SpaceIconPicker, iconText } from './SpaceIcon';

const report = (e: unknown) => toast(errorText(e instanceof ApiError ? e.code : 'internal'));

type TrackerKind = 'issue' | 'sprint' | 'startSprint';
type Kind = 'space' | 'project' | 'team' | 'editSpace' | TrackerKind;
interface Where {
  team?: string | null;
  space?: string | null;
  org?: string | null;
  edit?: Space | null;
  /** The tracker project of an issue or a sprint; without one, the dialog asks. */
  project?: string | null;
}

/** Which creation dialog is open, and where the new thing should go (shared by the sidebar, pages and ⌘K). */
const useCreate = create<{ kind: Kind | null } & Where>(() => ({ kind: null }));

export const openCreate = (kind: Kind, where: Where = {}) =>
  // Nothing from the previous dialog carries over.
  useCreate.setState({ kind, team: null, space: null, org: null, edit: null, project: null, ...where });
const close = () => useCreate.setState({ kind: null, edit: null });

/**
 * Where the person is (set by the sidebar from the page in view): the organization, and the team,
 * space and project of the page if it has them. "Create" and ⌘K put new things there.
 */
export const useHere = create<{ org: string | null; team: string | null; space: string | null; project: string | null }>(() => ({ org: null, team: null, space: null, project: null }));

/** A new board in the team (and space) given, else in the first team of the organization; then open it. */
export function useNewBoard(session: Session) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  return async (where: { org: string | null; team?: string | null; space?: string | null }) => {
    const teamId = where.team ?? teamsHere(session, where.org)[0]?.id;
    try {
      const b = await api.post<Board>('/boards', { teamId, spaceId: where.space ?? undefined });
      void qc.invalidateQueries({ queryKey: ['boards'] });
      void qc.invalidateQueries({ queryKey: ['spaces'] });
      navigate(`/b/${b.id}`);
    } catch (e) {
      report(e);
    }
  };
}

export function teamLabel(team: Team, t: (k: string) => string): string {
  return team.personal ? t('nav.personal') : team.name;
}

const TrackerCreate = lazy(() => import('../tracker/CreateHost'));
const isTracker = (k: Kind | null): k is TrackerKind => k === 'issue' || k === 'sprint' || k === 'startSprint';

/** Hosts the dialogs once per page. */
export function CreateDialogs({ session }: { session: Session }) {
  const state = useCreate();
  return (
    <>
      <SpaceDialog open={state.kind === 'space' || state.kind === 'editSpace'} session={session} org={state.org ?? null} team={state.team ?? null} edit={state.kind === 'editSpace' ? (state.edit ?? null) : null} />
      <ProjectDialog open={state.kind === 'project'} session={session} org={state.org ?? null} team={state.team ?? null} space={state.space ?? null} />
      <TeamDialog open={state.kind === 'team'} org={state.org ?? null} />
      {isTracker(state.kind) && (
        <Suspense fallback={null}>
          <TrackerCreate kind={state.kind} project={state.project ?? null} team={state.team ?? null} org={state.org ?? null} onClose={close} />
        </Suspense>
      )}
    </>
  );
}

/** Teams a new thing may go to: those of the organization in view, the personal team outside one. */
function teamsHere(session: Session, org: string | null): Team[] {
  const here = session.teams.filter((t) => (org ? t.orgId === org : t.orgId === null));
  return here.length ? here : session.teams;
}

function SpaceDialog({ open, session, org, team, edit }: { open: boolean; session: Session; org: string | null; team: string | null; edit: Space | null }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const teams = teamsHere(session, org);
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('');
  const [description, setDescription] = useState('');
  const [teamId, setTeamId] = useState('');
  const [closed, setClosed] = useState(false);
  useEffect(() => {
    if (!open) return;
    setClosed(false);
    setName(edit?.name ?? '');
    setIcon(edit?.icon ?? '');
    setDescription(edit?.description ?? '');
    setTeamId(edit?.teamId ?? team ?? teams[0]?.id ?? '');
    // Only when it opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, edit?.id]);
  const save = async () => {
    try {
      const body = { name: name.trim(), icon: icon.trim(), description: description.trim() };
      const s = edit ? await api.patch<Space>(`/spaces/${edit.id}`, body) : await api.post<Space>(`/teams/${teamId}/spaces`, { ...body, teamAccess: closed ? 'none' : 'edit' });
      void qc.invalidateQueries({ queryKey: ['spaces'] });
      close();
      if (!edit) navigate(`/s/${s.id}`);
    } catch (e) {
      report(e);
    }
  };
  return (
    <Modal open={open} onOpenChange={(v) => !v && close()} title={t(edit ? 'spaces.editTitle' : 'spaces.newTitle')} description={edit ? undefined : t('spaces.newHint')} width={480}>
      <form
        className="space-form"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <label className="field">
          <span>{t('spaces.name')}</span>
          <input className="input" autoFocus value={name} maxLength={120} onChange={(e) => setName(e.target.value)} placeholder={t('spaces.namePlaceholder')} />
        </label>
        <div className="field">
          <span>{t('spaces.icon')}</span>
          <SpaceIconPicker value={icon} onChange={setIcon} />
        </div>
        <label className="field">
          <span>{t('spaces.description')}</span>
          <textarea className="input" rows={3} value={description} maxLength={2000} onChange={(e) => setDescription(e.target.value)} placeholder={t('spaces.descriptionPlaceholder')} />
        </label>
        {!edit && teams.length > 1 && (
          <label className="field">
            <span>{t('spaces.team')}</span>
            <select className="input" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
              {teams.map((tm) => (
                <option key={tm.id} value={tm.id}>
                  {teamLabel(tm, t)}
                </option>
              ))}
            </select>
          </label>
        )}
        {!edit && !teams.find((tm) => tm.id === teamId)?.personal && (
          <label className="space-private-check">
            <input type="checkbox" checked={closed} onChange={(e) => setClosed(e.target.checked)} />
            <span>
              <b>
                <Lock size={13} /> {t('spaces.privateOption')}
              </b>
              <span className="muted">{t('spaces.privateOptionHint')}</span>
            </span>
          </label>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button className="btn primary" disabled={!name.trim() || !teamId}>
            {t(edit ? 'common.save' : 'common.create')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** A tracker project: name, key, and where — a team and one of its spaces. */
function ProjectDialog({ open, session, org, team, space }: { open: boolean; session: Session; org: string | null; team: string | null; space: string | null }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [teamId, setTeamId] = useState('');
  const [spaceId, setSpaceId] = useState('');
  // Other teams of the organization that work in it from the start (docs/teams-plan.md).
  const [also, setAlso] = useState<Set<string>>(new Set());
  const spaces = useTeamSpaces(open ? teamId : null);
  // The suggested team's organization, else the one in view: a project stays among its colleagues.
  const suggested = session.teams.find((tm) => tm.id === team);
  const teams = teamsHere(session, suggested ? suggested.orgId : org);
  const home = session.teams.find((tm) => tm.id === teamId);
  const orgTeams = useQuery({
    queryKey: ['orgs', home?.orgId, 'teams'],
    queryFn: () => api.get<{ id: string; name: string }[]>(`/orgs/${home!.orgId}/teams`),
    enabled: open && !!home?.orgId,
  });
  const others = (orgTeams.data ?? []).filter((tm) => tm.id !== teamId);
  useEffect(() => {
    if (!open) return;
    setName('');
    setKey('');
    setKeyTouched(false);
    setTeamId(team ?? teams[0]?.id ?? '');
    setSpaceId(space ?? '');
    setAlso(new Set());
    // Only when it opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (keyTouched || !name.trim()) return;
    const h = setTimeout(() => {
      api
        .get<{ key: string }>(`/tracker/suggest-key?name=${encodeURIComponent(name)}`)
        .then((r) => setKey(r.key))
        .catch(() => {});
    }, 200);
    return () => clearTimeout(h);
  }, [name, keyTouched]);
  // A space of another team is no choice: back to the team's default.
  const list = spaces.data ?? [];
  const chosen = list.find((s) => s.id === spaceId) ?? list.find((s) => s.isDefault);
  const createIt = async () => {
    try {
      const p = await api.post<TrackerProject>('/tracker/projects', {
        teamId,
        spaceId: chosen?.id,
        name: name.trim(),
        key: key.trim() || undefined,
        teamIds: others.filter((tm) => also.has(tm.id)).map((tm) => tm.id),
      });
      void qc.invalidateQueries({ queryKey: keys.projects });
      void qc.invalidateQueries({ queryKey: ['spaces'] });
      void qc.invalidateQueries({ queryKey: ['tracker', 'team'] });
      close();
      navigate(`/t/${p.id}`);
    } catch (e) {
      report(e);
    }
  };
  return (
    <Modal open={open} onOpenChange={(v) => !v && close()} title={t('tracker.newProject')} description={t('tracker.newProjectHint')} width={480}>
      <form
        className="space-form"
        onSubmit={(e) => {
          e.preventDefault();
          void createIt();
        }}
      >
        <label className="field">
          <span>{t('tracker.projectName')}</span>
          <input className="input" autoFocus value={name} maxLength={120} onChange={(e) => setName(e.target.value)} placeholder={t('tracker.projectNamePlaceholder')} />
        </label>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <label className="field" style={{ width: 140 }}>
            <span>{t('tracker.projectKey')}</span>
            <input
              className="input tr-key-input"
              value={key}
              maxLength={10}
              onChange={(e) => {
                setKeyTouched(true);
                setKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''));
              }}
            />
          </label>
          {teams.length > 1 && (
            <label className="field" style={{ flex: 1, minWidth: 160 }}>
              <span>{t('tracker.team')}</span>
              <select
                className="input"
                value={teamId}
                onChange={(e) => {
                  setTeamId(e.target.value);
                  setSpaceId('');
                  setAlso(new Set());
                }}
              >
                {teams.map((tm) => (
                  <option key={tm.id} value={tm.id}>
                    {teamLabel(tm, t)}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        {list.length > 1 && (
          <label className="field">
            <span>{t('spaces.space')}</span>
            <select className="input" value={chosen?.id ?? ''} onChange={(e) => setSpaceId(e.target.value)}>
              {list.map((s) => (
                <option key={s.id} value={s.id}>
                  {iconText(s.icon)}
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {others.length > 0 && (
          <fieldset className="field project-teams">
            <legend>{t('tracker.alsoTeams')}</legend>
            <label className="project-team-check">
              <input
                type="checkbox"
                checked={others.every((tm) => also.has(tm.id))}
                onChange={(e) => setAlso(new Set(e.target.checked ? others.map((tm) => tm.id) : []))}
              />
              <b>{t('tracker.allTeams')}</b>
            </label>
            {others.map((tm) => (
              <label key={tm.id} className="project-team-check">
                <input
                  type="checkbox"
                  checked={also.has(tm.id)}
                  onChange={(e) => {
                    const next = new Set(also);
                    if (e.target.checked) next.add(tm.id);
                    else next.delete(tm.id);
                    setAlso(next);
                  }}
                />
                {tm.name}
              </label>
            ))}
            <span className="muted tr-small">{t('tracker.alsoTeamsHint')}</span>
          </fieldset>
        )}
        <p className="muted tr-small" style={{ margin: 0 }}>
          {t('tracker.keyHint', { example: `${key || 'PAY'}-12` })}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button className="btn primary" disabled={!name.trim() || !teamId}>
            {t('common.create')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function TeamDialog({ open, org }: { open: boolean; org: string | null }) {
  const { t } = useTranslation();
  const refresh = useRefreshSession();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  useEffect(() => {
    if (open) setName('');
  }, [open]);
  const save = async () => {
    try {
      await api.post('/teams', { name: name.trim(), orgId: org });
      await refresh();
      void qc.invalidateQueries({ queryKey: ['spaces'] });
      close();
    } catch (e) {
      report(e);
    }
  };
  return (
    <Modal open={open} onOpenChange={(v) => !v && close()} title={t('dashboard.newTeam')} description={t('spaces.teamHint')} width={440}>
      <form
        className="space-form"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <label className="field">
          <span>{t('dashboard.teamName')}</span>
          <input className="input" autoFocus value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button className="btn primary" disabled={!name.trim()}>
            {t('common.create')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
